import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Wallet,
  TrendingUp,
  Coins,
  Layers,
  ArrowUpRight,
  CalendarDays,
  Users,
  Crown,
} from "lucide-react";
import api from "@/lib/api";
import { formatUSD, formatDate, cleanOperatorName, formatLocal } from "@/lib/utils";
import { formatUserLabel, shortRef } from "@/lib/formatUser";
import Pagination from "@/components/Pagination";
import StatusPill from "@/components/StatusPill";
import TransactionRowActions from "@/components/TransactionRowActions";
import WalletSummaryCard from "@/components/WalletSummaryCard";
import DashboardProviderStrip from "@/components/DashboardProviderStrip";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const REFRESH_MS = 30000;

const RANGE_KEYS = ["today", "yesterday", "7d", "30d", "month", "last_month", "custom"];

function useRangeOptions() {
  const { t } = useTranslation();
  return useMemo(() => ([
    { key: "today", label: t("dashboard.today") },
    { key: "yesterday", label: t("dashboard.range_yesterday", "Yesterday") },
    { key: "7d", label: t("dashboard.range_7d", "Last 7 Days") },
    { key: "30d", label: t("dashboard.range_30d", "Last 30 Days") },
    { key: "month", label: t("dashboard.month") },
    { key: "last_month", label: t("dashboard.range_last_month", "Last Month") },
    { key: "custom", label: t("dashboard.custom") },
  ]), [t]);
}

function KpiCard({ icon: Icon, label, value, sub, tone = "blue", testid }) {
  // Flat HaitiTalk-style — solid color, icon left, value right, small radius
  const tones = {
    blue:   { bg: "bg-[#2BA8F0]" },  // bright sky blue
    slate:  { bg: "bg-[#8A99A8]" },  // muted gray
    green:  { bg: "bg-[#7CB342]" },  // lime green
    purple: { bg: "bg-[#9C27B0]" },  // vivid purple
    amber:  { bg: "bg-[#F39C12]" },  // orange
  };
  const t = tones[tone] || tones.blue;
  return (
    <div
      data-testid={testid}
      className={`${t.bg} rounded-md p-5 min-h-[150px] flex items-center gap-4 transition-opacity hover:opacity-95`}
    >
      <Icon className="w-16 h-16 text-white/95 flex-shrink-0" strokeWidth={1.6} />
      <div className="ml-auto text-right min-w-0">
        <div
          className="text-3xl sm:text-4xl font-bold text-white leading-tight"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {value}
        </div>
        <div className="text-sm text-white/95 font-medium mt-1">{label}</div>
        {sub && <div className="text-[11px] text-white/80 mt-0.5 truncate max-w-[200px] ml-auto">{sub}</div>}
      </div>
    </div>
  );
}

function DateRangeFilter({ range, setRange, customFrom, setCustomFrom, customTo, setCustomTo, label }) {
  const { t } = useTranslation();
  const RANGE_OPTIONS = useRangeOptions();
  const [open, setOpen] = useState(false);
  const onPick = (key) => {
    setRange(key);
    if (key !== "custom") setOpen(false);
  };
  return (
    <div className="relative inline-block">
      <button
        data-testid="dashboard-range-button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm font-semibold"
      >
        <CalendarDays className="w-4 h-4 text-[#0F62FE]" />
        {label}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-64 rounded-lg border border-slate-200 bg-white shadow-lg z-40 py-1 text-sm">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                data-testid={`dashboard-range-${opt.key}`}
                onClick={() => onPick(opt.key)}
                className={`w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 text-left ${
                  range === opt.key ? "text-[#0F62FE] font-semibold bg-blue-50/40" : "text-slate-700"
                }`}
              >
                {opt.label}
                {range === opt.key && <span className="w-1.5 h-1.5 rounded-full bg-[#0F62FE]" />}
              </button>
            ))}
            {range === "custom" && (
              <div className="border-t border-slate-200 mt-1 p-3 space-y-2">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{t("dashboard.range_from", "From")}</label>
                  <input
                    type="date"
                    data-testid="dashboard-range-from"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="w-full h-9 px-2 rounded-md border border-slate-300 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{t("dashboard.range_to", "To")}</label>
                  <input
                    type="date"
                    data-testid="dashboard-range-to"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="w-full h-9 px-2 rounded-md border border-slate-300 text-sm"
                  />
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="w-full h-9 rounded-md bg-[#0F62FE] text-white text-xs font-semibold hover:bg-[#0050E6]"
                >
                  {t("common.apply")}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isMaster = user.role === "MASTER_AGENT";
  const isCollab = user.role === "COLLABORATOR";
  // Collaborators operate on the MASTER_ADMIN's platform-wide data, so
  // aggregates and network breakdowns render exactly like the admin view.
  const showsAdminView = user.role === "MASTER_ADMIN" || isCollab;

  const [stats, setStats] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [tab, setTab] = useState("transactions");
  const [funding, setFunding] = useState([]);
  const [fundingTotal, setFundingTotal] = useState(0);
  const [fundingPage, setFundingPage] = useState(1);
  const [fundingPageSize, setFundingPageSize] = useState(10);
  const [fundingLoaded, setFundingLoaded] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsPageSize, setTransactionsPageSize] = useState(10);
  const [transactionsLoaded, setTransactionsLoaded] = useState(false);
  const [threshold, setThreshold] = useState(100);

  const [range, setRange] = useState("today");
  const [customFrom, setCustomFrom] = useState(() => new Date(Date.now() - 7 * 86400e3).toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(() => new Date().toISOString().slice(0, 10));

  const intervalRef = useRef(null);

  const RANGE_OPTIONS = useRangeOptions();
  const rangeLabel = useMemo(() => {
    if (range === "custom") return `${customFrom} → ${customTo}`;
    return RANGE_OPTIONS.find((r) => r.key === range)?.label || t("dashboard.today");
  }, [range, customFrom, customTo, RANGE_OPTIONS, t]);

  const load = async () => {
    try {
      const params = { range };
      if (range === "custom") {
        params.date_from = customFrom;
        params.date_to = customTo;
      }
      const { data } = await api.get("/dashboard/stats", { params });
      setStats(data);
      setLastRefresh(new Date());
    } catch {
      /* interceptor handles auth */
    }
  };

  useEffect(() => {
    load();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, customFrom, customTo]);

  useEffect(() => {
    // Global app settings — pulls the configurable low-balance threshold.
    api.get("/app-settings")
      .then((r) => { if (r.data?.low_balance_threshold_usd != null) setThreshold(Number(r.data.low_balance_threshold_usd)); })
      .catch(() => {});
  }, []);

  const loadFunding = async () => {
    try {
      const skip = (fundingPage - 1) * fundingPageSize;
      const { data } = await api.get(
        `/wallet/ledger?paginated=true&limit=${fundingPageSize}&skip=${skip}`
      );
      setFunding(data.items || []);
      setFundingTotal(data.total || 0);
      setFundingLoaded(true);
    } catch {
      setFundingLoaded(true);
    }
  };

  useEffect(() => {
    if (tab === "balances") {
      setFundingLoaded(false);
      loadFunding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, fundingPage, fundingPageSize]);

  const loadTransactions = async () => {
    try {
      const skip = (transactionsPage - 1) * transactionsPageSize;
      const { data } = await api.get(
        `/topups?limit=${transactionsPageSize}&skip=${skip}`
      );
      setTransactions(data.items || []);
      setTransactionsTotal(data.total || 0);
      setTransactionsLoaded(true);
    } catch {
      setTransactionsLoaded(true);
    }
  };

  useEffect(() => {
    if (tab === "transactions") {
      setTransactionsLoaded(false);
      loadTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, transactionsPage, transactionsPageSize]);

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      {/* Compact greeting strip — HaitiTalk classic */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1
            className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {user.role === "MASTER_ADMIN"
              ? `${t("role.MASTER_ADMIN")} — ${t("dashboard.title")}`
              : isCollab
                ? `Collaborator — ${t("dashboard.title")}`
                : isMaster
                  ? `${t("role.MASTER_AGENT")} — ${t("dashboard.title")}`
                  : `${t("role.AGENT")} — ${t("dashboard.title")}`}
          </h1>
          <p className="text-slate-500 mt-0.5 text-xs flex items-center gap-2">
            {t("dashboard.welcome")}, <span className="font-semibold text-slate-700">{user.name}</span>
            {lastRefresh && (
              <span data-testid="dashboard-refresh-indicator" className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                {t("dashboard.last_updated")} · {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeFilter
            range={range}
            setRange={setRange}
            customFrom={customFrom}
            setCustomFrom={setCustomFrom}
            customTo={customTo}
            setCustomTo={setCustomTo}
            label={rangeLabel}
          />
          <Link
            to="/send-topup"
            data-testid="dashboard-send-topup-cta"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-[#2BA8F0] text-white text-sm font-semibold hover:bg-[#1F95DA]"
          >
            <ArrowUpRight className="w-4 h-4" /> {t("nav.send_topup")}
          </Link>
        </div>
      </div>

      {/* KPI cards: HaitiTalk-style coloured tops */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard
          testid="kpi-wallet-balance"
          icon={Wallet}
          label={t("dashboard.kpi_available_balance")}
          value={formatUSD(stats?.wallet_balance_usd ?? 0)}
          sub={isMaster ? `${t("dashboard.internal_pool", "Internal pool")}: ${formatUSD(stats?.internal_balance_usd ?? 0)}` : `${t("dashboard.master_wallet_hint")} · USD`}
          tone="blue"
        />
        <KpiCard
          testid="kpi-sales-today"
          icon={TrendingUp}
          label={`${t("dashboard.kpi_total_sales")} · ${rangeLabel}`}
          value={formatUSD(stats?.sales_range_usd ?? 0)}
          sub={`${stats?.successful_topups_range ?? 0} ${t("dashboard.successful_topups").toLowerCase()}`}
          tone="slate"
        />
        <KpiCard
          testid="kpi-commission-today"
          icon={Coins}
          label={t("dashboard.kpi_my_commissions")}
          value={formatUSD(stats?.commission_range_usd ?? 0)}
          sub={`${t("dashboard.lifetime", "Lifetime")}: ${formatUSD(stats?.commission_total_usd ?? 0)}`}
          tone="green"
        />
        <KpiCard
          testid="kpi-total-transactions"
          icon={Layers}
          label={t("dashboard.kpi_total_transactions")}
          value={stats?.total_transactions_range ?? 0}
          sub={`${t("dashboard.pending_topups")}: ${stats?.pending_topups ?? 0}`}
          tone="purple"
        />
      </div>

      {/* Master Wallet Balance — sits right below the primary KPI grid.
          Admin sees an "Add Funds" button that reuses the manual-funding
          endpoint (ledger category FUNDING_MANUAL). Balance auto-refreshes
          after each Add Funds and via the 30s dashboard poll. For MASTER_ADMIN
          we show `internal_balance_usd` (the DB wallet that funding/transfers
          actually mutate) rather than the DingConnect network balance. */}
      {user?.role === "MASTER_ADMIN" && (
        <WalletSummaryCard
          balanceUsd={stats?.internal_balance_usd}
          lastUpdated={lastRefresh?.toISOString()}
          threshold={threshold}
          canFund={true}
          onRefresh={load}
        />
      )}

      {/* Provider network snapshot — MASTER_ADMIN only, mirrors the
          data of /provider Dashboard tab in a compact form. */}
      {user?.role === "MASTER_ADMIN" && <DashboardProviderStrip />}

      {/* Delegated wallet view for COLLABORATOR — shows the Master Admin's
          real platform wallet with a clear "delegated access" label. Funding
          is disabled (an approval-gated action lives in Transfer Funds). */}
      {isCollab && (
        <WalletSummaryCard
          balanceUsd={stats?.internal_balance_usd}
          lastUpdated={lastRefresh?.toISOString()}
          threshold={threshold}
          canFund={false}
          onRefresh={load}
          delegatedLabel="Platform Wallet — Delegated Access"
        />
      )}

      {/* Network KPI strip — visible to MASTER_ADMIN + MASTER_AGENT + COLLABORATOR */}
      {(showsAdminView || isMaster) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="network-kpi-strip">
          {showsAdminView && (
            <KpiCard
              testid="kpi-master-agents-count"
              icon={Crown}
              label={t("dashboard.kpi_master_agents")}
              value={stats?.master_agents_count ?? 0}
              sub={t("role.MASTER_AGENT")}
              tone="amber"
            />
          )}
          <KpiCard
            testid="kpi-agents-count"
            icon={Users}
            label={showsAdminView ? t("dashboard.kpi_total_agents") : t("nav.agents")}
            value={stats?.agents_count ?? 0}
            sub={t("role.AGENT")}
            tone="blue"
          />
          {(stats?.network_breakdown?.length ?? 0) > 0 && (
            <KpiCard
              testid="kpi-top-performer"
              icon={TrendingUp}
              label={t("dashboard.kpi_top_performer")}
              value={stats?.network_breakdown?.[0]?.username || "—"}
              sub={`${formatUSD(stats?.network_breakdown?.[0]?.sales ?? 0)} · ${stats?.network_breakdown?.[0]?.count ?? 0}`}
              tone="green"
            />
          )}
          <KpiCard
            testid="kpi-network-volume"
            icon={Coins}
            label={`${t("dashboard.kpi_network_volume")} · ${rangeLabel}`}
            value={formatUSD((stats?.network_breakdown || []).reduce((sum, r) => sum + (r.sales || 0), 0))}
            sub={`${(stats?.network_breakdown || []).reduce((s, r) => s + (r.count || 0), 0)} ${t("dashboard.successful_topups").toLowerCase()}`}
            tone="purple"
          />
        </div>
      )}

      {/* Network breakdown table — MASTER_ADMIN/COLLAB see Master Agents; MASTER_AGENT sees Agents */}
      {(stats?.network_breakdown?.length ?? 0) > 0 && (showsAdminView || isMaster) && (
        <div className="bg-white rounded-md border border-slate-200" data-testid="network-breakdown-card">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {showsAdminView ? t("dashboard.sales_by_master", "Sales by master agent") : t("dashboard.sales_by_agent", "Sales by sub-agent")}
              </div>
              <div className="text-sm text-slate-700 mt-0.5">{rangeLabel}</div>
            </div>
            <Link
              to={showsAdminView ? "/reports" : "/subagent-transactions"}
              className="text-xs font-semibold text-[#0F62FE] hover:underline"
            >
              {t("dashboard.view_all")} →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="network-breakdown-table">
              <thead>
                <tr>
                  <th className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-2 border-b border-slate-200">#</th>
                  <th className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-2 border-b border-slate-200">
                    {showsAdminView ? t("role.MASTER_AGENT") : t("role.AGENT")}
                  </th>
                  <th className="bg-slate-50 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-2 border-b border-slate-200">{t("dashboard.kpi_total_sales")}</th>
                  <th className="bg-slate-50 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-2 border-b border-slate-200">{t("dashboard.transactions")}</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.network_breakdown || []).slice(0, 8).map((r, idx) => (
                  <tr key={r.master_agent_id || r.agent_id || r.username} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-5 py-2 text-slate-500 font-mono text-xs">{idx + 1}</td>
                    <td className="px-5 py-2 text-slate-900 font-semibold">
                      {r.username}
                      {r.agent_code && <span className="ml-2 font-mono text-[10px] text-slate-500">{r.agent_code}</span>}
                    </td>
                    <td className="px-5 py-2 text-right font-semibold text-emerald-700">{formatUSD(r.sales)}</td>
                    <td className="px-5 py-2 text-right text-slate-700">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 px-5 flex items-center gap-1">
          <TabButton active={tab === "transactions"} onClick={() => setTab("transactions")} testid="tab-transactions">
            {t("dashboard.transactions")}
          </TabButton>
          <TabButton active={tab === "balances"} onClick={() => setTab("balances")} testid="tab-balances">
            {t("dashboard.funding_history")}
          </TabButton>
          <Link
            to="/reports"
            className="ml-auto text-xs font-semibold text-[#0F62FE] hover:underline py-3 px-2"
          >
            {t("nav.reports")} →
          </Link>
        </div>

        {tab === "transactions" && (
          <>
            <TransactionsTable rows={transactions} isMaster={isMaster} loaded={transactionsLoaded} />
            <Pagination
              page={transactionsPage}
              pageSize={transactionsPageSize}
              total={transactionsTotal}
              onPage={setTransactionsPage}
              onPageSize={setTransactionsPageSize}
              testId="transactions-pagination"
            />
          </>
        )}
        {tab === "balances" && (
          <>
            <BalancesHistory rows={funding} loaded={fundingLoaded} />
            <Pagination
              page={fundingPage}
              pageSize={fundingPageSize}
              total={fundingTotal}
              onPage={setFundingPage}
              onPageSize={setFundingPageSize}
              testId="funding-pagination"
            />
          </>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, testid, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`relative px-4 py-3 text-sm font-semibold transition-colors ${
        active ? "text-[#0F62FE]" : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
      {active && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#0F62FE] rounded-full" />}
    </button>
  );
}

function TransactionsTable({ rows, isMaster, loaded = true }) {
  const { t } = useTranslation();
  const columns = [
    { key: "id", label: t("dashboard.col_id", "ID"), align: "left" },
    { key: "created", label: t("dashboard.col_created", "Created"), align: "left" },
    { key: "product", label: t("dashboard.col_product", "Product"), align: "left" },
    { key: "phone", label: t("dashboard.col_phone", "Phone"), align: "left" },
    ...(isMaster ? [{ key: "agent", label: t("role.AGENT"), align: "left" }] : []),
    { key: "amount_paid", label: t("dashboard.col_amount_paid", "Amount paid"), align: "right" },
    { key: "customer_receives", label: t("dashboard.col_customer_receives", "Customer receives"), align: "right" },
    { key: "status", label: t("common.status"), align: "left" },
    { key: "actions", label: t("common.actions"), align: "left" },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="recent-transactions-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`bg-slate-50 ${c.align === "right" ? "text-right" : "text-left"} text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200 whitespace-nowrap`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!loaded && (
            <tr>
              <td colSpan={columns.length} className="px-5 py-10 text-center text-sm text-slate-500">
                Loading…
              </td>
            </tr>
          )}
          {loaded && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-5 py-10 text-center text-sm text-slate-500">
                {t("dashboard.no_transactions")}
              </td>
            </tr>
          )}
          {rows.map((t) => {
            const localCcy = (t.local_currency || "USD").toUpperCase();
            const paidLocal = t.customer_pays_local != null
              ? t.customer_pays_local
              : Number(t.retail_price_usd || 0) * Number(t.exchange_rate || 1);
            const shortId = (t.id || "").slice(0, 8).toUpperCase();
            return (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-[11px] text-slate-500 whitespace-nowrap">{shortId}</td>
                <td className="px-4 py-3 text-slate-700 text-xs whitespace-nowrap">{formatDate(t.created_at)}</td>
                <td className="px-4 py-3 text-slate-700 text-xs whitespace-nowrap">{cleanOperatorName(t.operator_name)}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap">{t.phone_number}</td>
                {isMaster && <td className="px-4 py-3 text-slate-700 text-xs whitespace-nowrap">{formatUserLabel({ name: t.agent_name, username: t.agent_username, agent_code: t.agent_code, role: t.agent_role || "AGENT" })}</td>}
                <td className="px-4 py-3 font-semibold text-slate-900 text-right whitespace-nowrap">{formatLocal(paidLocal, localCcy)}</td>
                <td className="px-4 py-3 text-emerald-700 font-semibold text-xs text-right whitespace-nowrap">
                  {t.receive_value > 0 && t.receive_currency
                    ? `${Number(t.receive_value).toFixed(2)} ${t.receive_currency}`
                    : "—"}
                </td>
                <td className="px-4 py-3 whitespace-nowrap"><StatusPill status={t.status} /></td>
                <td className="px-4 py-3 whitespace-nowrap"><TransactionRowActions topup={t} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const SOURCE_LABEL = {
  FUNDING_CARD: { label: "Card funding", tone: "emerald" },
  FUNDING: { label: "Manual funding", tone: "emerald" },
  TOPUP_HOLD: { label: "Provider topup", tone: "rose" },
  TOPUP_TRUEUP: { label: "Topup adjustment", tone: "slate" },
  TOPUP_REVERSAL: { label: "Reversal", tone: "emerald" },
  TRANSFER_OUT: { label: "Transfer to agent", tone: "rose" },
  TRANSFER_IN: { label: "Transfer from master", tone: "emerald" },
  WITHDRAWAL_OUT: { label: "Withdrawal", tone: "rose" },
  WITHDRAWAL_IN: { label: "Withdrawal from agent", tone: "emerald" },
  ADJUSTMENT: { label: "Adjustment", tone: "slate" },
};

function BalancesHistory({ rows, loaded }) {
  const columns = [
    { key: "ref", label: "Ref", align: "left" },
    { key: "created", label: "Created", align: "left" },
    { key: "type", label: "Type", align: "left" },
    { key: "source", label: "Source", align: "left" },
    { key: "counterparty", label: "Counterparty", align: "left" },
    { key: "amount", label: "Amount", align: "right" },
    { key: "available_after", label: "Available after", align: "right" },
    { key: "note", label: "Note", align: "left" },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="balances-history-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`bg-slate-50 ${c.align === "right" ? "text-right" : "text-left"} text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-5 py-3 border-b border-slate-200 whitespace-nowrap`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!loaded && (
            <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-slate-500">Loading…</td></tr>
          )}
          {loaded && rows.length === 0 && (
            <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-slate-500">No wallet movements yet.</td></tr>
          )}
          {rows.map((e) => {
            const isCredit = e.type === "CREDIT";
            const amount = Number(e.amount || 0);
            const meta = SOURCE_LABEL[e.category] || { label: (e.category || "—").replace(/_/g, " "), tone: "slate" };
            const toneClasses = {
              emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
              rose: "bg-rose-50 text-rose-700 border-rose-200",
              slate: "bg-slate-100 text-slate-700 border-slate-200",
            }[meta.tone];
            const refShort = shortRef(e.ref_id || e.id, 10);
            const hasCp = !!e.counterparty_id;
            const cp = hasCp ? formatUserLabel({
              name: e.counterparty_name,
              username: e.counterparty_username,
              agent_code: e.counterparty_agent_code,
              role: e.counterparty_role,
            }) : null;
            // Smart note: if the row has counterparty enrichment, use the
            // canonical label. Otherwise fall back to the raw note.
            let noteDisplay = e.note || "";
            if (hasCp) {
              const cat = e.category || "";
              if (cat === "TRANSFER_OUT")      noteDisplay = `Transfer to ${cp}`;
              else if (cat === "TRANSFER_IN")  noteDisplay = `Transfer from ${cp}`;
              else if (cat === "WITHDRAWAL_OUT") noteDisplay = `Withdrawal to ${cp}`;
              else if (cat === "WITHDRAWAL_IN")  noteDisplay = `Withdrawal from ${cp}`;
              if (e.note && !noteDisplay.includes(e.note)) {
                noteDisplay = `${noteDisplay} · ${e.note}`;
              }
            }
            return (
              <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 font-mono text-[11px] text-slate-600" title={e.ref_id || e.id}>{refShort}</td>
                <td className="px-5 py-3 text-slate-700 text-xs whitespace-nowrap">{formatDate(e.created_at)}</td>
                <td className="px-5 py-3">
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${
                    isCredit ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                  }`}>
                    {isCredit ? "Credit" : "Debit"}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-md border ${toneClasses}`}>
                    {meta.label}
                  </span>
                </td>
                <td className="px-5 py-3 text-xs text-slate-800 font-semibold">
                  {cp || "—"}
                </td>
                <td className={`px-5 py-3 font-semibold text-right ${isCredit ? "text-emerald-700" : "text-rose-700"}`}>
                  {isCredit ? "+" : "−"}{formatUSD(amount)}
                </td>
                <td className="px-5 py-3 text-slate-900 font-semibold text-right">
                  {e.balance_after != null ? formatUSD(e.balance_after) : "—"}
                </td>
                <td className="px-5 py-3 text-slate-500 text-xs max-w-md truncate" title={noteDisplay}>{noteDisplay || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
